import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {SpeechModel} from 'app/blocks/service/speech/speech-model.interface';

@Injectable({
    providedIn: 'root'
})
export class DefaultSpeechModelService implements SpeechModel {
    private recognition: any;
    private transcriptionSubject = new BehaviorSubject<string>('');
    private mediaRecorder: MediaRecorder | null = null;
    private audioStream: MediaStream | null = null;
    private _isRecording = false;
    private audioChunks: Blob[] = [];

    transcription$ = this.transcriptionSubject.asObservable();
    get isRecording(): boolean {
        return this._isRecording;
    }

    constructor() {
        if ('webkitSpeechRecognition' in window) {
            this.recognition = new (window as any).webkitSpeechRecognition();
            this.recognition.continuous = true;
            this.recognition.interimResults = false;
            this.recognition.lang = 'en-US';

            this.recognition.onresult = (event) => {
                const arr = Array.from(event.results);
                if (arr.length > 0) {
                    const results = this.transcriptionSubject.value + ' ' + arr[arr.length - 1][0].transcript;
                    this.transcriptionSubject.next(results);
                }
            };
        }
    }

    async startRecording(transcription): Promise<void> {
        try {
            // Start speech recognition
            if (this.recognition && transcription) {
                this.recognition.start();
            }

            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 2,
                    sampleRate: 96000
                }
            });

            this.mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus',
                audioBitsPerSecond: 96000
            });

            // Clear any previous audio chunks
            this.audioChunks = [];
            this.transcriptionSubject.next('');

            this.mediaRecorder.addEventListener('dataavailable', (event) => {
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            });

            this.mediaRecorder.addEventListener('error', (error) => {
                console.error('MediaRecorder error:', error);
            });

            this.mediaRecorder.start(100);

            this._isRecording = true;
        } catch (error) {
            console.error('Error starting default recording:', error);
            throw error;
        }
    }

    async stopRecording(): Promise<string> {
        // Stop speech recognition
        if (this.recognition) {
            this.recognition.stop();
        }

        // Get the final audio chunk
        this.mediaRecorder.stop();

        // Stop all tracks in the audio stream
        this.audioStream?.getTracks().forEach((track) => track.stop());
        this.audioStream = null;

        this._isRecording = false;

        await new Promise((resolve) => setTimeout(resolve, 1000));

        return this.transcriptionSubject.value;
    }

    async getAudioData(): Promise<Blob> {
        if (this.audioChunks.length === 0) {
            console.log('No audio chunks available');
            return null;
        }
        console.log('Creating audio blob from', this.audioChunks.length, 'chunks');
        const audioBlob = new Blob(this.audioChunks, {type: 'audio/webm;codecs=opus'});
        this.audioChunks = [];
        return audioBlob;
    }
}
